import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {WarehouseTableData} from 'insights/warehouse/warehouseModelsDataLayout';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import omitKey from 'util/omitKey';

import SQLFilterOperatorSelector from './SQLFilterOperatorSelector';
import SQLFilterValueInput from './SQLFilterValueInput';
import {
  DEFAULT_COLUMN_FILTER_PROPERTIES,
  FILTERED_COLUMN_TYPE_IS_SUPPORTED,
  FORBIDDEN_FILTER_VALUE_STRINGS,
} from './SQLTemplateFormConstants';
import {AvailableColumn, ColumnFilter, ColumnFilterType} from './SQLTemplateFormTypes';
import {getColumnDisplayName, getColumnIcon, validateFormValues} from './SQLTemplateFormUtil';

export const CLEAR_SELECTED_COLUMN_SENTINEL = {};

const CLEAR_FILTER_OPTION = {
  name: 'Clear selection',
  value: CLEAR_SELECTED_COLUMN_SENTINEL,
};
const SELECTED_OPTIONS_HEADER = {
  name: 'Included fields',
  isHeader: true,
  className: 'schema-section',
};
const NOT_SELECTED_OPTIONS_HEADER = {
  name: 'Excluded fields',
  isHeader: true,
  className: 'schema-section',
};

export const DISABLED_OPTION_DATA = {
  disabled: true,
  disabledMessage: "This column's type is not supported as a filter.",
};

const SELECTED_KEY = '_selected';

export function getColumnSelectorMenuOptions(
  availableColumns: Array<AvailableColumn>,
  columnFilter: ColumnFilter | null
): Array<OptionData> {
  const allColumnOptionDatas = availableColumns.map((column, index) => ({
    icon: getColumnIcon(column),
    name: getColumnDisplayName(column),
    [SELECTED_KEY]: column.selected,
    value: index,
    ...(FILTERED_COLUMN_TYPE_IS_SUPPORTED[column.columnData.type] ? {} : DISABLED_OPTION_DATA),
  }));

  const [selectedOptionDatas, notSelectedOptionDatas] = _.partition(
    allColumnOptionDatas,
    (optionData) => optionData._selected
  );

  return [
    ...(columnFilter != null ? [CLEAR_FILTER_OPTION] : []),
    ...(selectedOptionDatas.length > 0
      ? [
          SELECTED_OPTIONS_HEADER,
          ...selectedOptionDatas.map((optionData) => omitKey(optionData, SELECTED_KEY)),
        ]
      : []),
    ...(notSelectedOptionDatas.length > 0
      ? [
          NOT_SELECTED_OPTIONS_HEADER,
          ...notSelectedOptionDatas.map((optionData) => omitKey(optionData, SELECTED_KEY)),
        ]
      : []),
  ];
}

export interface SQLFilterSelectorProps {
  availableColumns: Array<AvailableColumn>;
  columnFilters: Array<ColumnFilter>;
  disabled: boolean;
  selectedTable: WarehouseTableData | null;
  updateColumnFilters: (columnFilters: Array<ColumnFilter>) => void;
}

export default function SQLFilterSelector(props: SQLFilterSelectorProps): JSX.Element {
  const columnFilter = props.columnFilters[0] ?? null;
  const filteredColumn = props.availableColumns[columnFilter?.filteredColumnIndex] ?? null;
  const disabled = props.disabled || props.availableColumns.length === 0;

  function handleUpdateColumnFilter(columnFilter: ColumnFilter): void {
    props.updateColumnFilters([columnFilter]);
  }

  function handleClearFilter(): void {
    props.updateColumnFilters([]);
  }

  function handleUpdateFilteredColumnIndex(filteredColumnIndex: number): void {
    handleUpdateColumnFilter({
      ...DEFAULT_COLUMN_FILTER_PROPERTIES,
      filteredColumnIndex,
    });
  }

  function handleUpdateOperator(filterType: ColumnFilterType): void {
    assert(columnFilter != null, 'Expected filter to exist before being able to select an filterType');
    handleUpdateColumnFilter({
      ...columnFilter,
      filterType,
    });
  }

  function handleUpdateValues(values: Array<string>, rawValues: string): void {
    assert(columnFilter != null, 'Expected filter to exist before being able to update values');
    handleUpdateColumnFilter({
      ...columnFilter,
      rawValues,
      validationError: validateFormValues(values, 'Filter values', FORBIDDEN_FILTER_VALUE_STRINGS),
      values,
    });
  }

  function onSelectColumn(value: number | typeof CLEAR_SELECTED_COLUMN_SENTINEL): void {
    if (value === CLEAR_SELECTED_COLUMN_SENTINEL) {
      handleClearFilter();
    } else {
      handleUpdateFilteredColumnIndex(value as number);
    }
  }

  const columnMenu = (
    <SearchableMenu
      height={MenuHeight.MEDIUM}
      optionDatas={getColumnSelectorMenuOptions(props.availableColumns, columnFilter)}
      onSelect={onSelectColumn}
      scrollable={true}
      selectedValue={columnFilter?.filteredColumnIndex}
      width={MenuWidth.LARGE}
    />
  );

  assertAndContinue(
    columnFilter == null || props.selectedTable != null,
    'Found non-null columnFilter but did not get a non-null selectedTable'
  );

  return (
    <div className={css(styles.rootWrapper)}>
      <SelectDropdown
        // Increase size of dropdown button to match size of inputs from SQLFilterValueInput
        buttonSize={ButtonSize.MEDIUM}
        className={css(styles.subSelectorWrapper)}
        disabled={disabled}
        displayName={filteredColumn != null ? getColumnDisplayName(filteredColumn) : 'Select a field'}
        menu={columnMenu}
      />
      {!disabled && columnFilter != null && props.selectedTable != null && (
        <>
          <div className={css(styles.subSelectorWrapper)}>
            <SQLFilterOperatorSelector
              buttonSize={ButtonSize.MEDIUM}
              selectedType={columnFilter.filterType}
              onUpdateOperator={(filterType) => {
                handleUpdateOperator(filterType);
              }}
            />
          </div>
          <SQLFilterValueInput
            columnFilter={columnFilter}
            filteredColumn={filteredColumn}
            updateValues={handleUpdateValues}
          />
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  rootWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subSelectorWrapper: {
    paddingBottom: 8,
    paddingRight: 8,
    whiteSpace: 'nowrap',
  },
});
